<script setup lang="ts">
import { RrButton, RrModal } from '@xjlv2/ui'
import type { MaterialType, SaasMaterial } from '../../types'

const props = defineProps<{
  open: boolean
  material: SaasMaterial | null
}>()

const emit = defineEmits<{
  'update:open': [val: boolean]
  'open-references': [material: SaasMaterial, event: MouseEvent]
}>()

const typeMap: Record<MaterialType, string> = {
  IMAGE: '图片',
  ARTICLE: '文稿',
  AUDIO: '音频',
  VIDEO: '视频'
}

function typeLabel(type?: MaterialType) {
  return type ? (typeMap[type] ?? type) : '-'
}

function fileSize(value?: number | null) {
  if (value == null) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function compactValue(value?: number | null, unit = '') {
  return value == null ? '-' : `${value}${unit}`
}
</script>

<template>
  <RrModal
    :open="open"
    :title="material?.name || '素材预览'"
    :width="860"
    :footer="null"
    @update:open="(val) => emit('update:open', val)"
  >
    <section v-if="material" class="material-preview-stage">
      <div class="preview-stage-container">
        <img
          v-if="material.type === 'IMAGE' && material.fileUrl"
          :src="material.fileUrl"
          :alt="material.name"
          class="stage-media"
        />
        <video
          v-else-if="material.type === 'VIDEO' && material.fileUrl"
          :src="material.fileUrl"
          controls
          class="stage-media"
        />
        <audio
          v-else-if="material.type === 'AUDIO' && material.fileUrl"
          :src="material.fileUrl"
          controls
          class="stage-audio"
        />
        <div v-else class="stage-document">
          <strong class="document-title">{{ material.title || material.name }}</strong>
          <p class="document-content">{{ material.content || material.description || '该文稿未填写正文。' }}</p>
          <a v-if="material.fileUrl" :href="material.fileUrl" target="_blank" rel="noreferrer" class="document-link">打开附件</a>
        </div>
      </div>

      <div class="preview-meta-bar">
        <span>{{ typeLabel(material.type) }}</span>
        <span>{{ fileSize(material.fileSize) }}</span>
        <span>{{ compactValue(material.width, ' px') }} × {{ compactValue(material.height, ' px') }}</span>
        <RrButton
          type="link"
          size="small"
          class="reference-detail-btn"
          @click="emit('open-references', material, $event)"
        >
          被引用 {{ material.referenceCount ?? 0 }} 次（查看明细）
        </RrButton>
      </div>
    </section>
  </RrModal>
</template>

<style scoped>
.material-preview-stage {
  display: flex;
  flex-direction: column;
}

.preview-stage-container {
  display: grid;
  min-height: 300px;
  max-height: 65vh;
  place-items: center;
  background: var(--rr-color-canvas);
  border-radius: 6px;
  padding: 20px;
  overflow: hidden;
}

.stage-media {
  max-width: 100%;
  max-height: 58vh;
  border-radius: 4px;
  object-fit: contain;
}

.stage-audio {
  width: min(600px, 100%);
}

.stage-document {
  display: grid;
  width: min(560px, 100%);
  gap: 12px;
  border-radius: 6px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  padding: 24px;
}

.document-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.document-content {
  color: var(--rr-color-text);
  line-height: 1.65;
  font-size: 14px;
  white-space: pre-wrap;
  margin: 0;
  max-height: 40vh;
  overflow-y: auto;
}

.document-link {
  color: var(--rr-color-primary);
  font-weight: 500;
  text-decoration: underline;
}

.preview-meta-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 4px 0;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.preview-meta-bar span + span,
.preview-meta-bar span + :deep(.rr-button) {
  border-left: 1px solid var(--rr-color-border);
  padding-left: 12px;
}

.reference-detail-btn {
  text-decoration: underline;
}
</style>
